import { Injectable } from '@angular/core';
import { CrtData, CrtDataType, CrtTypeMetier } from '../../models/data';
import { CrtDataLr, DonneeLrType } from '../../models/dataLr';
import { EcartStatus, Lr, TypeCourbe, TypeExtrapolation, TypeLR } from '../../models/lr';
import { CrtLrDataInterneRaw, CrtLrDataReduitRaw, CrtLrDataSimpleRaw, LrDataPointRaw, LrRaw } from '../../models/lrRaw';
import { ObjetMetierStatut, ObjetMetierTypeEnum } from '../../models/objetMetier';
import { CrtLrReglageCollection } from '../../models/reglage-lr';

/**
 * Service pour mapper l'objet LR envoyé par le back avec le modèle de donnée du front
 */
@Injectable({
  providedIn: 'root',
})
export class CrtLrMapperService {
  /**
   * Map une LrRaw ver sune Lr
   * @param lrRaw
   * @param crtDatas
   */
  public mapLrRawToLr(lrRaw: LrRaw, crtDatas: CrtData[]): Lr {
    return {
      type: ObjetMetierTypeEnum.LR,
      id: lrRaw.id,
      actualisation: lrRaw.actualisation,
      descriptionHierarNiv1: lrRaw.descriptionHierarNiv1,
      descriptionHierarNiv2: lrRaw.descriptionHierarNiv2,
      descriptionHierarNiv3: lrRaw.descriptionHierarNiv3,
      descriptionMetier: lrRaw.descriptionMetier,
      libelleInstallation: lrRaw.libelleInstallation,
      libelleSite: lrRaw.libelleSite,
      dkCodeSite: lrRaw.dkCodeSite,
      dkCodeInstallation: lrRaw.dkCodeInstallation,
      priorite: lrRaw.priorite,
      etatConcordance: lrRaw.etatConcordance,
      maintenance: lrRaw.maintenance,
      blocage: lrRaw.blocage,
      invalidation: lrRaw.invalidation,
      presencePersonnel: lrRaw.presencePersonnel,
      statut: lrRaw.statut,
      generique: lrRaw.generique,
      datas: this.mapLrRawToCrtDataLr(lrRaw, crtDatas),
      echelleMaxX: lrRaw.loiRegulationDTO ? lrRaw.loiRegulationDTO.echelleMaxX : null,
      echelleMaxY: lrRaw.loiRegulationDTO ? lrRaw.loiRegulationDTO.echelleMaxY : null,
      echelleMinX: lrRaw.loiRegulationDTO ? lrRaw.loiRegulationDTO.echelleMinX : null,
      echelleMinY: lrRaw.loiRegulationDTO ? lrRaw.loiRegulationDTO.echelleMinY : null,
      pasX: lrRaw.loiRegulationDTO ? lrRaw.loiRegulationDTO.pasX : null,
      pasY: lrRaw.loiRegulationDTO ? lrRaw.loiRegulationDTO.pasY : null,
      typeExtrapolation: lrRaw.loiRegulationDTO
        ? this.mapLrRawTypeCourbeToTypeExtrapolation(lrRaw.loiRegulationDTO.typeExtrapolation)
        : null,
      typeCourbe: lrRaw.loiRegulationDTO
        ? this.mapLrRawTypeCourbeToTypeCourbe(lrRaw.loiRegulationDTO.typeCourbe)
        : null,
      descriptionAxeX: lrRaw.loiRegulationDTO.descriptionAxeX,
      descriptionAxeY: lrRaw.loiRegulationDTO.descriptionAxeY,
      tempExt: lrRaw.loiRegulationDTO.tempExterieure,
      programmesHoraires: lrRaw.loiRegulationDTO.programmesHoraires,
      ecartStatus: this.mapEcartStatus(lrRaw.loiRegulationDTO ? lrRaw.loiRegulationDTO.ecartStatus : null),
      typeLR: lrRaw.typeLR,
      image: lrRaw.loiRegulationDTO.image,
      copilotReady: lrRaw.loiRegulationDTO.copilotReady,
      transmetteur: lrRaw.loiRegulationDTO.transmetteur,
      idSdd: lrRaw.loiRegulationDTO.idSdd,
      etatEnvoi: lrRaw.etatEnvoi,
      identifiantOh: lrRaw.identifiantOh,
      idHierarNiv1: lrRaw.idHierarNiv1,
      idHierarNiv2: lrRaw.idHierarNiv2,
      idHierarNiv3: lrRaw.idHierarNiv3,
      etatFonctionnel: lrRaw.etatFonctionnel,
    };
  }

  /**
   * Map les status des lr en fonctions de si la LR a des réglages
   * @param lrs liste de loi de régulations
   * @param reglagesLr les réglages LR du site
   */
  public mapStatusToLrList(lrs: Lr[], reglagesLr: CrtLrReglageCollection): Lr[] {
    return lrs.map((lr) => this.mapStatusToLr(lr, reglagesLr));
  }

  public mapStatusToLr(lr: Lr, reglagesLr: CrtLrReglageCollection) {
    if (reglagesLr[lr.id]) {
      lr.statut = ObjetMetierStatut.ENVOI;
    }

    return lr;
  }

  /**
   * récupere la liste de CrtDataLr depuis une lrRaw
   * @param lr
   * @param crtDatas
   */
  private mapLrRawToCrtDataLr(lr: LrRaw, crtDatas: CrtData[]): CrtDataLr[] {
    let crtDataLr: CrtDataLr[] = [];

    if (lr.loiRegulationDTO === null) return crtDataLr;

    if (lr.loiRegulationDTO.minimum) {
      crtDataLr.push(
        this.mapDonneInterneToCrtData(
          lr.loiRegulationDTO.minimum,
          lr,
          DonneeLrType.MIN,
          null,
          lr.loiRegulationDTO.minimum.ecartStatus
        )
      );
    }

    if (lr.loiRegulationDTO.maximum) {
      crtDataLr.push(
        this.mapDonneInterneToCrtData(
          lr.loiRegulationDTO.maximum,
          lr,
          DonneeLrType.MAX,
          null,
          lr.loiRegulationDTO.maximum.ecartStatus
        )
      );
    }

    lr.loiRegulationDTO.reduits.forEach((reduit: CrtLrDataReduitRaw) => {
      crtDataLr.push(
        this.mapDonneInterneToCrtData(reduit.offset, lr, DonneeLrType.R, reduit.numReduit, reduit.offset.ecartStatus)
      );
    });

    lr.loiRegulationDTO.courbe.points.forEach((point: LrDataPointRaw) => {
      if (point.coordX !== null) {
        crtDataLr.push(
          this.mapDonneInterneToCrtData(point.coordX, lr, DonneeLrType.X, point.numPoint, point.coordX.ecartStatus)
        );
      }

      if (point.coordY !== null) {
        crtDataLr.push(
          this.mapDonneInterneToCrtData(point.coordY, lr, DonneeLrType.Y, point.numPoint, point.coordY.ecartStatus)
        );
      }
    });

    crtDatas.forEach((crtData) => {
      crtDataLr.push(this.mapDonneSimpleToCrtData(crtData, lr));
    });

    return crtDataLr;
  }

  /**
   * Mapper une donnée simple en crt data lr
   * @param crtData la donnée crt
   * @param lr la lrRaw
   */
  private mapDonneSimpleToCrtData(crtData: CrtData, lr: LrRaw): CrtDataLr {
    const donneeSimpleRaw: CrtLrDataSimpleRaw = lr.loiRegulationDTO.donneesSimples.find(
      (donneSimple) => donneSimple.id === crtData.id
    );

    return {
      ...crtData,
      etatEnvoi: crtData.etatEnvoi === true ? 2 : 0,
      valeurConseille: donneeSimpleRaw.valeurConseille,
      typeDonnee: DonneeLrType.SIMPLE,
      numeroDonnee: null,
      ecartStatus: donneeSimpleRaw ? this.mapEcartStatus(donneeSimpleRaw.ecartStatus) : null,
    };
  }

  /**
   * mapper une donnée interne LR en crtData
   * @param {CrtLrDataInterneRaw} donneInterne
   * @param {string} descriptionAxeX
   * @param {string} descriptionAxeY
   * @param {string} descriptionSite
   * @param {string} descriptionSiteSecondaire
   * @returns {CrtData}
   */
  private mapDonneInterneToCrtData(
    donneInterne: CrtLrDataInterneRaw,
    lr: LrRaw,
    type: DonneeLrType,
    numeroDonnee: number,
    ecartStatus: number
  ): CrtDataLr {
    return {
      id: donneInterne.id,
      descriptionDonnee: donneInterne.description,
      descriptionHierarNiv1: lr.descriptionHierarNiv1,
      descriptionHierarNiv2: lr.descriptionHierarNiv2,
      descriptionHierarNiv3: lr.descriptionHierarNiv3,
      descriptionMetier: lr.descriptionMetier,
      type: this.mapLrRawTypeDonneeToCrtTypeDonnee(donneInterne.typeDonnee),
      typeValeur: null, //TODO : Voir comment l'initialiser
      unite: donneInterne.unite,
      formatAffichage: donneInterne.formatAff,
      prioriAff: donneInterne.prioriAff,
      valeurCRT: donneInterne.valeurReglage,
      valeurSite: donneInterne.valeurSite,
      valeurConseille: donneInterne.valeurConseille,
      horodateCourant: null, //TODO : Voir comment l'initialiser
      horodateDerniereSaisie: donneInterne.derniereMaj,
      estFavoris: false,
      estHistorique: false,
      associationLibelle: null, //TODO : A initialiser
      discordant: donneInterne.etatDiscordance,
      precisionDiscordance: donneInterne.precisionDiscordance,
      informationsHistorique: null,
      plageDeSaisie: donneInterne.hasPlageSaisie,
      plageMinimum: donneInterne.plageMin,
      plageMaximum: donneInterne.plageMax,
      maintenance: lr.maintenance,
      blocage: lr.blocage,
      blocageSite: null,
      invalidation: lr.invalidation,
      typeDonnee: type,
      numeroDonnee: numeroDonnee,
      typeMetier: lr.typeLR === TypeLR.LOI_GENERIQUE_CONSIGNE ? CrtTypeMetier.LGI : CrtTypeMetier.LGU,
      ecartStatus: this.mapEcartStatus(ecartStatus),
      libelleSite: lr.libelleSite,
      libelleInstallation: lr.libelleInstallation,
      etatEnvoi: donneInterne.etatEnvoi,
      dkCodeInstallation: lr.dkCodeInstallation,
      priorite: null,
      disparition: false,
      identifiantOh: lr.identifiantOh,
      idHierarNiv1: lr.idHierarNiv1,
      idHierarNiv2: lr.idHierarNiv2,
      idHierarNiv3: lr.idHierarNiv3,
      idSiteAlarming: null,
      dkCodeSiteAlarming: null,
      libelleSiteAlarming: null,
      estSysteme: null,
      etatFonctionnel: lr.etatFonctionnel,
    };
  }

  /**
   * mapper le type de donnée LR en CrtDataType
   * @param {number} lrTypeDonne
   * @returns {CrtDataType}
   */
  private mapLrRawTypeDonneeToCrtTypeDonnee(lrTypeDonne: number): CrtDataType {
    switch (lrTypeDonne) {
      case 0:
        return CrtDataType.TR;
      case 1:
        return CrtDataType.TM;
      case 2:
        return CrtDataType.TMT;
      case 3:
        return CrtDataType.TRT;
      case 4:
        return CrtDataType.TS;
      case 5:
        return CrtDataType.TSP;
      case 6:
        return CrtDataType.TC;
      case 7:
        return CrtDataType.TM;
      case 8:
        return CrtDataType.TR;
      default:
        return null;
    }
  }

  /**
   * mapper le type de courbe
   */
  private mapLrRawTypeCourbeToTypeCourbe(lrRawTypeCourbe): TypeCourbe {
    switch (lrRawTypeCourbe) {
      case 0:
        return TypeCourbe.MONOTONE_CROISSANTE;
      case 1:
        return TypeCourbe.MONOTONE_DECROISSANTE;
      case 2:
        return TypeCourbe.MONOTONE_STRICTE_CROISSANTE;
      case 3:
        return TypeCourbe.MONOTONE_STRICTE_DECROISSANTE;
      case 4:
        return TypeCourbe.Y_LIBRE;
      default:
        return null;
    }
  }

  /**
   * mapper le type d'extrapolation
   */
  private mapLrRawTypeCourbeToTypeExtrapolation(lrRawTypeCourbe): TypeExtrapolation {
    switch (lrRawTypeCourbe) {
      case 0:
        return TypeExtrapolation.PROLONGATION;
      case 1:
        return TypeExtrapolation.LIMITATION;
      default:
        return null;
    }
  }

  private mapEcartStatus(ecartStatus: number): EcartStatus {
    switch (ecartStatus) {
      case 0:
        return EcartStatus.Invisible;
      case 1:
        return EcartStatus.Gris;
      case 2:
        return EcartStatus.Orange;
      case 3:
        return EcartStatus.Vert;
      default:
        return EcartStatus.Invisible;
    }
  }
}
